import { getStorage, getValue as getValueAdapter, setValue as setValueAdapter } from './storageAdapter';
import { withDbLock } from './dbLock';

const LEGACY_STOCK_KEY = 'stock';
const DIRTY_STOCK_KEY = 'stock_dirty_map';
const LEGACY_ITEM_BARCODES_KEY = 'item_barcodes_map';
const SQLITE_IN_CLAUSE_CHUNK = 900;
const STOCK_UPSERT_BATCH_SIZE = 400;
const MOBILE_EXECUTE_SET_BATCH_SIZE = 32;
const ITEM_BARCODE_INSERT_SQL = `INSERT OR REPLACE INTO item_barcodes (barcode, stock_code) VALUES (?, ?)`;

const UPSERT_STOCK_SQL = `
    INSERT INTO stock_items (
        plu, barcode, description, description2, department, sub_dep, sub_dep2,
        selling_incl, qty, archived, fast_line, has_image, updated_at, payload
    )
    VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        ON CONFLICT(plu) DO UPDATE SET
        barcode = excluded.barcode,
                                description = excluded.description,
                                description2 = excluded.description2,
                                department = excluded.department,
                                sub_dep = excluded.sub_dep,
                                sub_dep2 = excluded.sub_dep2,
                                selling_incl = excluded.selling_incl,
                                qty = excluded.qty,
                                archived = excluded.archived,
                                fast_line = excluded.fast_line,
                                has_image = excluded.has_image,
                                updated_at = excluded.updated_at,
                                payload = excluded.payload
`;

let schemaReady = false;
let migrationChecked = false;

const toLower = (value) => String(value || '').toLowerCase();
const toStringOrNull = (value) => {
    if (value === undefined || value === null || value === '') return null;
    return String(value);
};
const toNumberOrNull = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};
const toIntBool = (value) => (value === true || value === 1 || value === '1' ? 1 : 0);
const sortByDescription = (a, b) => String(a?.Description || '').localeCompare(String(b?.Description || ''));

const normalizeQuery = (filters = {}, searchText = '') => ({
    category: toLower(filters.category),
    subCategory: toLower(filters.subCategory),
    sub2Category: toLower(filters.sub2Category),
    description: toLower(searchText || filters.description),
});

const matchesQuery = (item, query, includeDescription2 = true) => {
    if (query.category && toLower(item?.Department) !== query.category) return false;
    if (query.subCategory && toLower(item?.SubDep) !== query.subCategory) return false;
    if (query.sub2Category && toLower(item?.Subdep2) !== query.sub2Category) return false;
    if (!query.description) return true;

    const text = query.description;
    if (toLower(item?.Description).includes(text)) return true;
    if (includeDescription2 && toLower(item?.Description2).includes(text)) return true;
    if (toLower(item?.PLU).includes(text)) return true;
    if (toLower(item?.barcode).includes(text)) return true;
    return false;
};

const normalizeStockRecord = (item) => {
    if (!item) return null;
    const plu = toStringOrNull(item.PLU ?? item.plu);
    if (!plu) return null;

    const updatedAt = toStringOrNull(item.updated_at ?? item.UpdatedAt) || new Date().toISOString();
    const payload = JSON.stringify(item);
    return {
        plu,
        barcode: toStringOrNull(item.barcode ?? item.Barcode),
        description: toStringOrNull(item.Description ?? item.description) || plu,
        description2: toStringOrNull(item.Description2 ?? item.description2),
        department: toStringOrNull(item.Department ?? item.department),
        sub_dep: toStringOrNull(item.SubDep ?? item.sub_dep),
        sub_dep2: toStringOrNull(item.Subdep2 ?? item.SubDep2 ?? item.sub_dep2),
        selling_incl: toNumberOrNull(item.SellingIncl ?? item.selling_incl),
        qty: toNumberOrNull(item.actualQty ?? item.QTY ?? item.qty),
        archived: toIntBool(item.archived),
        fast_line: toIntBool(item.FastLine ?? item.fast_line),
        has_image: toIntBool(Boolean(item.Image) || item.has_image),
        updated_at: updatedAt,
        payload,
    };
};

const mapRowToItem = (row) => {
    if (!row) return null;
    if (row.payload) {
        try {
            const parsed = JSON.parse(row.payload);
            // Plan §A5 (storage rehydration boundary): coerce `archived` and
            // `FastLine` so the payload path produces the same canonical shapes
            // as the reconstructed-row path below. Without this, a row whose
            // server payload had `archived: 0` (or missing field) would round-
            // trip through JSON.stringify/parse and defeat the grid's strict
            // `=== false` filter — exactly the live bug class.
            if (parsed && typeof parsed === 'object') {
                const fastLineRaw = parsed.FastLine ?? parsed.fast_line;
                parsed.archived = parsed.archived === true
                    || parsed.archived === 1
                    || parsed.archived === '1'
                    || parsed.archived === 'true';
                parsed.FastLine = (fastLineRaw === true
                    || fastLineRaw === 1
                    || fastLineRaw === '1') ? 1 : 0;
            }
            return parsed;
        } catch (_) {
            // Fall through to reconstructed row.
        }
    }
    return {
        PLU: row.plu,
        barcode: row.barcode,
        Description: row.description,
        Description2: row.description2,
        Department: row.department,
        SubDep: row.sub_dep,
        Subdep2: row.sub_dep2,
        SellingIncl: row.selling_incl,
        QTY: row.qty,
        archived: row.archived === 1,
        FastLine: row.fast_line === 1 ? 1 : 0,
        updated_at: row.updated_at,
    };
};

const getCountValue = (row) => {
    if (!row) return 0;
    const raw = row.total ?? row.count ?? row['COUNT(*)'];
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
};

const toPositiveNumber = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return parsed;
};

const ensureSchema = async (storage) => {
    if (schemaReady || storage?.platform === 'indexeddb') return;

    const statements = [
        `CREATE TABLE IF NOT EXISTS stock_items (
                                                    plu TEXT PRIMARY KEY,
                                                    barcode TEXT,
                                                    description TEXT NOT NULL,
                                                    description2 TEXT,
                                                    department TEXT,
                                                    sub_dep TEXT,
                                                    sub_dep2 TEXT,
                                                    selling_incl REAL,
                                                    qty REAL,
                                                    archived INTEGER DEFAULT 0,
                                                    fast_line INTEGER DEFAULT 0,
                                                    has_image INTEGER DEFAULT 0,
                                                    updated_at TEXT NOT NULL,
                                                    payload TEXT NOT NULL
         )`,
        `CREATE INDEX IF NOT EXISTS idx_stock_barcode ON stock_items(barcode)`,
        `CREATE INDEX IF NOT EXISTS idx_stock_department ON stock_items(department, sub_dep, sub_dep2)`,
        `CREATE INDEX IF NOT EXISTS idx_stock_updated_at ON stock_items(updated_at)`,
        `CREATE INDEX IF NOT EXISTS idx_stock_fast_line ON stock_items(fast_line, archived)`,
        `CREATE TABLE IF NOT EXISTS stock_dirty (
                                                    plu TEXT PRIMARY KEY,
                                                    local_updated_at TEXT NOT NULL,
                                                    pending_sales INTEGER DEFAULT 0,
                                                    version INTEGER DEFAULT 1,
                                                    last_sale_id TEXT,
                                                    qty_delta REAL DEFAULT 0,
                                                    updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
         )`,
        `CREATE INDEX IF NOT EXISTS idx_stock_dirty_pending ON stock_dirty(pending_sales, local_updated_at)`,
        `CREATE TABLE IF NOT EXISTS item_barcodes (
                                                    barcode TEXT PRIMARY KEY,
                                                    stock_code TEXT NOT NULL
         )`,
        `CREATE INDEX IF NOT EXISTS idx_item_barcodes_stock ON item_barcodes(stock_code)`,
    ];

    for (const sql of statements) {
        await storage.run(sql, []);
    }
    schemaReady = true;
};

const flattenBarcodeItems = (items) => {
    const rows = [];
    const seen = new Set();
    for (const entry of Array.isArray(items) ? items : []) {
        const stockCode = toStringOrNull(entry?.plu ?? entry?.stock_code ?? entry?.PLU);
        if (!stockCode) continue;
        const barcodes = Array.isArray(entry?.barcodes) ? entry.barcodes : [];
        for (const raw of barcodes) {
            const barcode = toStringOrNull(raw);
            if (!barcode) continue;
            const key = toLower(barcode);
            if (seen.has(key)) continue;
            seen.add(key);
            rows.push({ barcode, stock_code: stockCode });
        }
    }
    return rows;
};

const runTransaction = async (storage, callback) => {
    if (storage?.platform === 'indexeddb') {
        return callback();
    }

    return withDbLock(async () => {
        let ownsTransaction = false;
        try {
            await storage.run('BEGIN IMMEDIATE TRANSACTION', []);
            ownsTransaction = true;
        } catch (beginError) {
            const msg = String(beginError?.message || '').toLowerCase();
            if (!msg.includes('within a transaction')) {
                throw beginError;
            }
            // Already in a transaction from another serialized flow; join it.
        }

        try {
            const result = await callback();
            if (ownsTransaction) {
                await storage.run('COMMIT', []);
            }
            return result;
        } catch (error) {
            if (ownsTransaction) {
                try {
                    await storage.run('ROLLBACK', []);
                } catch (_) {
                    // Ignore rollback errors to preserve original exception.
                }
            }
            throw error;
        }
    });
};

const setLegacyStock = async (items) => {
    await setValueAdapter(LEGACY_STOCK_KEY, items, 'main_store');
};

const getLegacyStock = async () => {
    const stock = await getValueAdapter(LEGACY_STOCK_KEY, 'main_store');
    return Array.isArray(stock) ? stock : [];
};

const upsertLegacyStock = async (items) => {
    const existing = await getLegacyStock();
    const byPlu = new Map(
        existing
            .filter(item => item && item.PLU !== undefined && item.PLU !== null)
            .map(item => [toLower(item.PLU), item])
    );

    for (const item of items) {
        if (!item || item.PLU === undefined || item.PLU === null) continue;
        byPlu.set(toLower(item.PLU), item);
    }

    const merged = Array.from(byPlu.values()).sort(sortByDescription);
    await setLegacyStock(merged);
    return merged.length;
};

const mapStockRecordToParams = (record) => ([
    record.plu,
    record.barcode,
    record.description,
    record.description2,
    record.department,
    record.sub_dep,
    record.sub_dep2,
    record.selling_incl,
    record.qty,
    record.archived,
    record.fast_line,
    record.has_image,
    record.updated_at,
    record.payload,
]);

const chunkRecords = (records, size = STOCK_UPSERT_BATCH_SIZE) => {
    const safeSize = Math.max(1, Number(size) || STOCK_UPSERT_BATCH_SIZE);
    const chunks = [];
    for (let i = 0; i < records.length; i += safeSize) {
        chunks.push(records.slice(i, i + safeSize));
    }
    return chunks;
};

const canUseMobileExecuteSet = (storage) => (
    storage?.platform === 'mobile' &&
    typeof storage?.dbConnection?.executeSet === 'function'
);

const runMobileExecuteSet = async (storage, records, transaction = true) => {
    const values = records.map(mapStockRecordToParams);
    if (!values.length) return;

    try {
        await storage.dbConnection.executeSet([{
            statement: UPSERT_STOCK_SQL,
            values,
        }], transaction, 'no', true);
    } catch (error) {
        const msg = String(error?.message || '').toLowerCase();
        if (transaction && msg.includes('within a transaction')) {
            await storage.dbConnection.executeSet([{
                statement: UPSERT_STOCK_SQL,
                values,
            }], false, 'no', true);
            return;
        }
        throw error;
    }
};

const runUpsertChunk = async (storage, records, transaction = true) => {
    if (!records.length) return;

    if (canUseMobileExecuteSet(storage)) {
        await runMobileExecuteSet(storage, records, transaction);
        return;
    }

    const statements = records.map(record => ({
        sql: UPSERT_STOCK_SQL,
        params: mapStockRecordToParams(record),
    }));

    if (typeof storage.runBatch === 'function') {
        await storage.runBatch(statements, { transaction });
        return;
    }

    for (const statement of statements) {
        await storage.run(statement.sql, statement.params);
    }
};

const upsertSqlStock = async (storage, items, replace = false) => {
    await ensureSchema(storage);
    const records = (Array.isArray(items) ? items : [])
        .map(normalizeStockRecord)
        .filter(Boolean);
    const chunkSize = storage?.platform === 'mobile'
        ? MOBILE_EXECUTE_SET_BATCH_SIZE
        : STOCK_UPSERT_BATCH_SIZE;
    const chunks = chunkRecords(records, chunkSize);

    // Replace-all needs atomicity. Incremental upserts avoid explicit BEGIN/COMMIT to
    // prevent transaction collisions with concurrent DB work on the shared connection.
    if (replace) {
        await runTransaction(storage, async () => {
            await storage.run('DELETE FROM stock_items', []);
            for (const chunk of chunks) {
                // Already inside an explicit transaction from runTransaction.
                await runUpsertChunk(storage, chunk, false);
            }
        });
        return records.length;
    }

    for (const chunk of chunks) {
        await runUpsertChunk(storage, chunk, true);
    }

    return records.length;
};

const ensureMigratedFromLegacy = async (storage) => {
    if (migrationChecked || storage?.platform === 'indexeddb') return;
    migrationChecked = true;

    await ensureSchema(storage);

    const countRow = await storage.get('SELECT COUNT(*) AS total FROM stock_items', []);
    if (getCountValue(countRow) > 0) return;

    const legacyStock = await getLegacyStock();
    if (!legacyStock.length) return;

    await upsertSqlStock(storage, legacyStock, true);
};

const getLegacyDirtyMap = async () => {
    const map = await getValueAdapter(DIRTY_STOCK_KEY, 'main_store');
    if (!map || typeof map !== 'object' || Array.isArray(map)) return {};
    return map;
};

const setLegacyDirtyMap = async (map) => {
    await setValueAdapter(DIRTY_STOCK_KEY, map || {}, 'main_store');
};

const normalizePluDeltas = (deltas = []) => {
    const source = Array.isArray(deltas) ? deltas : [];
    const byPlu = new Map();

    for (const delta of source) {
        const plu = toStringOrNull(delta?.plu ?? delta?.PLU ?? delta?.Plu);
        if (!plu) continue;

        const qtyDelta = toPositiveNumber(delta?.qtyDelta ?? delta?.qty ?? delta?.Qty ?? delta?.QTY);
        if (qtyDelta <= 0) continue;

        const key = toLower(plu);
        const prev = byPlu.get(key);
        if (prev) {
            prev.qtyDelta = Number(prev.qtyDelta) + qtyDelta;
        } else {
            byPlu.set(key, { plu, qtyDelta });
        }
    }

    return Array.from(byPlu.values());
};

const buildInClause = (items = []) => {
    const safeItems = (Array.isArray(items) ? items : []).filter(Boolean);
    if (safeItems.length === 0) return { clause: '', params: [] };
    const placeholders = safeItems.map(() => '?').join(', ');
    return {
        clause: `(${placeholders})`,
        params: safeItems,
    };
};

const normalizeUniquePluList = (pluList = []) => {
    const source = Array.isArray(pluList) ? pluList : [];
    const unique = new Set();
    for (const plu of source) {
        const normalized = toStringOrNull(plu);
        if (!normalized) continue;
        unique.add(toLower(normalized));
    }
    return Array.from(unique);
};

const isTooManySqlVariablesError = (error) => String(error?.message || '').toLowerCase().includes('too many sql variables');

const runInClauseChunks = async (items = [], handler, initialChunkSize = SQLITE_IN_CLAUSE_CHUNK) => {
    const source = Array.isArray(items) ? items : [];
    if (!source.length) return;
    if (typeof handler !== 'function') return;

    let chunkSize = Math.max(1, Number(initialChunkSize) || SQLITE_IN_CLAUSE_CHUNK);
    let index = 0;

    while (index < source.length) {
        const chunk = source.slice(index, index + chunkSize);
        try {
            await handler(chunk);
            index += chunk.length;
        } catch (error) {
            if (!isTooManySqlVariablesError(error) || chunkSize <= 1) {
                throw error;
            }
            chunkSize = Math.max(1, Math.floor(chunkSize / 2));
        }
    }
};

const buildSqlFilters = (filters = {}, searchText = '', includeDescription2 = true) => {
    const query = normalizeQuery(filters, searchText);
    const where = ['archived = 0'];
    const params = [];

    if (query.category) {
        where.push('lower(department) = ?');
        params.push(query.category);
    }
    if (query.subCategory) {
        where.push('lower(sub_dep) = ?');
        params.push(query.subCategory);
    }
    if (query.sub2Category) {
        where.push('lower(sub_dep2) = ?');
        params.push(query.sub2Category);
    }
    if (query.description) {
        const likeValue = `%${query.description}%`;
        const searchParts = [
            'lower(description) LIKE ?',
            'lower(plu) LIKE ?',
            'lower(barcode) LIKE ?',
        ];
        const searchParams = [likeValue, likeValue, likeValue];
        if (includeDescription2) {
            searchParts.splice(1, 0, 'lower(description2) LIKE ?');
            searchParams.splice(1, 0, likeValue);
        }
        where.push(`(${searchParts.join(' OR ')})`);
        params.push(...searchParams);
    }

    return { whereSql: where.length ? `WHERE ${where.join(' AND ')}` : '', params, query };
};

const queryLegacyPaged = async (filters = {}, searchText = '', limit = 50, offset = 0, includeDescription2 = true) => {
    const query = normalizeQuery(filters, searchText);
    const stock = await getLegacyStock();
    const filtered = stock
        .filter(item => item && (item.archived === false || item.archived === 0 || item.archived === undefined))
        .filter(item => matchesQuery(item, query, includeDescription2))
        .sort(sortByDescription);

    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(0, Number(limit) || 0);
    const rows = safeLimit > 0 ? filtered.slice(safeOffset, safeOffset + safeLimit) : filtered.slice(safeOffset);
    return { rows, total: filtered.length };
};

const querySqlPaged = async (storage, filters = {}, searchText = '', limit = 50, offset = 0, includeDescription2 = true) => {
    await ensureSchema(storage);
    await ensureMigratedFromLegacy(storage);

    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(0, Number(limit) || 0);
    const { whereSql, params } = buildSqlFilters(filters, searchText, includeDescription2);

    const countSql = `SELECT COUNT(*) AS total FROM stock_items ${whereSql}`;
    const dataSql = `SELECT payload FROM stock_items ${whereSql} ORDER BY description LIMIT ? OFFSET ?`;
    const countRow = await storage.get(countSql, params);
    const rows = await storage.query(dataSql, [...params, safeLimit, safeOffset]);

    return {
        rows: (Array.isArray(rows) ? rows : []).map(mapRowToItem).filter(Boolean),
        total: getCountValue(countRow),
    };
};

const StockRepository = {
    async replaceAllStock(items) {
        const payload = Array.isArray(items) ? items : [];
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            await setLegacyStock(payload);
            return payload.length;
        }

        await upsertSqlStock(storage, payload, true);
        return payload.length;
    },

    async upsertStockBatch(items) {
        const payload = Array.isArray(items) ? items : [];
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            return upsertLegacyStock(payload);
        }

        await ensureMigratedFromLegacy(storage);
        return upsertSqlStock(storage, payload, false);
    },

    /**
     * Apply one page of stock items coming from the paginated
     * `/stock/{DB}/page` endpoint.
     *
     * Skips rows whose PLU has pending local sales
     * (see reconcileServerStockUpdates in Storage) and clears the
     * dirty flag for rows that the server has caught up with. Returns
     * `{ applied, skipped }` so callers (e.g. the Stock Vuex module)
     * can commit only the surviving updates into Vuex state and emit
     * progress events.
     *
     * The wrapper exists so the Stock module does not have to
     * orchestrate dirty-reconciliation itself per page — keep
     * `replaceAllStock` and `upsertStockBatch` untouched for callers
     * that want raw batch semantics.
     *
     * @param {Array<Object>} items page of raw server stock items
     * @param {Object} [deps] injection seam for tests
     * @param {Function} [deps.reconcile] override for the dirty reconcile step
     * @param {Function} [deps.upsert] override for the SQL upsert step
     * @returns {Promise<{applied: Array<Object>, skipped: Array<Object>}>}
     */
    async applyPaginatedStockPage(items, deps = {}) {
        const source = Array.isArray(items) ? items : [];
        if (source.length === 0) {
            return { applied: [], skipped: [] };
        }

        const reconcile = typeof deps.reconcile === 'function'
            ? deps.reconcile
            : async (rows) => {
                const { Storage: storageFacade } = await import('./Storage');
                return storageFacade.reconcileServerStockUpdates(rows);
            };
        const upsert = typeof deps.upsert === 'function'
            ? deps.upsert
            : async (rows) => StockRepository.upsertStockBatch(rows);

        const reconciliation = await reconcile(source);
        const applyUpdates = Array.isArray(reconciliation?.applyUpdates)
            ? reconciliation.applyUpdates
            : [];

        if (applyUpdates.length > 0) {
            await upsert(applyUpdates);
        }

        const clearDirtyPlu = Array.isArray(reconciliation?.clearDirtyPlu)
            ? reconciliation.clearDirtyPlu
            : [];
        if (clearDirtyPlu.length > 0) {
            try {
                const { Storage: storageFacade } = await import('./Storage');
                await storageFacade.clearDirtyStock(clearDirtyPlu);
            } catch (err) {
                console.warn('[StockRepository] Failed to clear dirty stock after page apply:', err);
            }
        }

        return {
            applied: applyUpdates,
            skipped: Array.isArray(reconciliation?.skippedUpdates)
                ? reconciliation.skippedUpdates
                : [],
        };
    },

    async getAllStock() {
        const result = [];
        for await (const chunk of this.getAllStockPaginated({ chunkSize: 500 })) {
            if (Array.isArray(chunk)) result.push(...chunk);
        }
        return result;
    },

    /**
     * Async-generator version of getAllStock that yields one chunk at a time.
     * Lets callers either stream chunks as they arrive (memory-friendly) or
     * collect them all via `for await`. Default chunkSize is 500 rows.
     *
     * Yields between page fetches to keep the JS main thread responsive on
     * low-memory devices — prevents ANR on 10k+ SKU stores (2026-06-26 incident).
     */
    /**
     * Count rows in `stock_items`. Used by the sync integrity report to
     * detect "API returned N but local store has M" mismatches after a
     * full paginated sync completes. Cheap on both backends: SQLite uses
     * `COUNT(*)` (plu is the PK so the index covers it); IndexedDB legacy
     * returns the in-memory legacy blob's length.
     *
     * Plan §B5: caller passes the result to `console.warn` when it differs
     * from `firstPage.total` — warn-only, never blocking.
     *
     * @returns {Promise<number|null>} row count, or null on read failure
     */
    async countAllStock() {
        try {
            const storage = await getStorage();
            if (storage.platform === 'indexeddb') {
                const legacy = await getLegacyStock();
                return Array.isArray(legacy) ? legacy.length : 0;
            }
            await ensureMigratedFromLegacy(storage);
            const rows = await storage.query('SELECT COUNT(*) AS total FROM stock_items', []);
            return getCountValue(rows && rows[0]);
        } catch (err) {
            console.warn('[StockRepository] countAllStock failed:', err?.message || err);
            return null;
        }
    },

    async *getAllStockPaginated({ chunkSize = 500 } = {}) {
        const storage = await getStorage();
        if (storage.platform === 'indexeddb') {
            const legacy = await getLegacyStock();
            yield legacy;
            return;
        }

        await ensureMigratedFromLegacy(storage);
        let offset = 0;
        while (true) {
            const rows = await storage.query(
                'SELECT payload FROM stock_items ORDER BY description ASC LIMIT ? OFFSET ?',
                [chunkSize, offset]
            );
            if (!Array.isArray(rows) || rows.length === 0) break;
            const mapped = rows.map(mapRowToItem).filter(Boolean);
            yield mapped;
            if (rows.length < chunkSize) break;
            // Yield to event loop so the main thread can process input events
            // / paint frames between page fetches. Without this, 20 sequential
            // SQL queries back-to-back blocked the UI thread for >5s on SUNMI
            // V2s_GL and triggered ANR (Application Not Responding) kill at
            // 17:36 on 2026-06-26.
            await new Promise(resolve => setTimeout(resolve, 0));
            offset += chunkSize;
        }
    },

    async getByPlu(plu) {
        const key = toStringOrNull(plu);
        if (!key) return null;
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            const stock = await getLegacyStock();
            return stock.find(item => toLower(item?.PLU) === toLower(key)) || null;
        }

        await ensureMigratedFromLegacy(storage);
        const row = await storage.get('SELECT payload FROM stock_items WHERE lower(plu) = lower(?) LIMIT 1', [key]);
        return mapRowToItem(row);
    },

    async getByBarcode(barcode) {
        const key = toStringOrNull(barcode);
        if (!key) return null;
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            try {
                const map = await getValueAdapter(LEGACY_ITEM_BARCODES_KEY);
                const stockCode = map && typeof map === 'object'
                    ? (map[key] || map[toLower(key)])
                    : null;
                if (stockCode) {
                    const stock = await getLegacyStock();
                    const byPlu = stock.find(item => toLower(item?.PLU) === toLower(stockCode));
                    if (byPlu) return byPlu;
                }
            } catch (_) { /* ignore map miss */ }
            const stock = await getLegacyStock();
            return stock.find(item => toLower(item?.barcode) === toLower(key)) || null;
        }

        await ensureMigratedFromLegacy(storage);

        try {
            const barcodeResult = await storage.get(
                `SELECT stock_code FROM item_barcodes WHERE lower(barcode) = lower(?) LIMIT 1`,
                [key]
            );
            if (barcodeResult && barcodeResult.stock_code) {
                const row = await storage.get(
                    'SELECT payload FROM stock_items WHERE lower(plu) = lower(?) LIMIT 1',
                    [barcodeResult.stock_code]
                );
                const item = mapRowToItem(row);
                if (item) return item;
            }
        } catch (e) {
            // table may be missing on older DBs until ensureSchema runs
        }

        const row = await storage.get(
            'SELECT payload FROM stock_items WHERE lower(barcode) = lower(?) LIMIT 1',
            [key]
        );
        return mapRowToItem(row);
    },

    /**
     * Full replace of local additional barcodes from
     * GET /stock/{DB}/barcodes → { items: [{ plu, barcodes: [] }] }.
     */
    async replaceAllItemBarcodes(items) {
        const rows = flattenBarcodeItems(items);
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            const map = {};
            for (const row of rows) {
                map[row.barcode] = row.stock_code;
                map[toLower(row.barcode)] = row.stock_code;
            }
            await setValueAdapter(LEGACY_ITEM_BARCODES_KEY, map);
            return rows.length;
        }

        await ensureSchema(storage);
        const chunkSize = storage?.platform === 'mobile'
            ? MOBILE_EXECUTE_SET_BATCH_SIZE
            : STOCK_UPSERT_BATCH_SIZE;
        const chunks = chunkRecords(rows, chunkSize);

        await runTransaction(storage, async () => {
            await storage.run('DELETE FROM item_barcodes', []);
            for (const chunk of chunks) {
                if (canUseMobileExecuteSet(storage)) {
                    const values = chunk.map((r) => [r.barcode, r.stock_code]);
                    try {
                        await storage.dbConnection.executeSet([{
                            statement: ITEM_BARCODE_INSERT_SQL,
                            values,
                        }], false, 'no', true);
                    } catch (error) {
                        for (const r of chunk) {
                            await storage.run(ITEM_BARCODE_INSERT_SQL, [r.barcode, r.stock_code]);
                        }
                    }
                } else if (typeof storage.runBatch === 'function') {
                    await storage.runBatch(
                        chunk.map((r) => ({
                            sql: ITEM_BARCODE_INSERT_SQL,
                            params: [r.barcode, r.stock_code],
                        })),
                        { transaction: false }
                    );
                } else {
                    for (const r of chunk) {
                        await storage.run(ITEM_BARCODE_INSERT_SQL, [r.barcode, r.stock_code]);
                    }
                }
            }
        });

        return rows.length;
    },

    async getFastLine(limit = 0, offset = 0) {
        const safeLimit = Math.max(0, Number(limit) || 0);
        const safeOffset = Math.max(0, Number(offset) || 0);
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            const stock = await getLegacyStock();
            const fastLine = stock
                .filter(item => item && item.FastLine === 1 && (item.archived === false || item.archived === 0 || item.archived === undefined))
                .sort(sortByDescription);
            if (!safeLimit) return fastLine;
            return fastLine.slice(safeOffset, safeOffset + safeLimit);
        }

        await ensureMigratedFromLegacy(storage);
        const sql = safeLimit
            ? 'SELECT payload FROM stock_items WHERE fast_line = 1 AND archived = 0 ORDER BY description LIMIT ? OFFSET ?'
            : 'SELECT payload FROM stock_items WHERE fast_line = 1 AND archived = 0 ORDER BY description';
        const rows = await storage.query(sql, safeLimit ? [safeLimit, safeOffset] : []);
        return (Array.isArray(rows) ? rows : []).map(mapRowToItem).filter(Boolean);
    },

    async queryStockPage(filters = {}, limit = 50, offset = 0) {
        const storage = await getStorage();
        if (storage.platform === 'indexeddb') {
            return queryLegacyPaged(filters, '', limit, offset, false);
        }
        return querySqlPaged(storage, filters, '', limit, offset, false);
    },

    async searchStockPage(filters = {}, text = '', limit = 50, offset = 0, includeDescription2 = true) {
        const storage = await getStorage();
        if (storage.platform === 'indexeddb') {
            return queryLegacyPaged(filters, text, limit, offset, includeDescription2);
        }
        return querySqlPaged(storage, filters, text, limit, offset, includeDescription2);
    },

    async applyLocalStockDeltas(stockDeltas, options = {}) {
        const {
            localUpdatedAt = new Date().toISOString(),
            storage: providedStorage = null,
            useTransaction = true,
        } = options || {};

        const deltas = normalizePluDeltas(stockDeltas);
        if (deltas.length === 0) return [];

        const storage = providedStorage || await getStorage();

        if (storage.platform === 'indexeddb') {
            const stock = await getLegacyStock();
            if (!Array.isArray(stock) || stock.length === 0) return [];

            const deltaByPlu = new Map(deltas.map(d => [toLower(d.plu), d.qtyDelta]));
            const updated = [];
            const next = stock.map(item => {
                const key = toLower(item?.PLU);
                if (!deltaByPlu.has(key)) return item;

                const decrement = deltaByPlu.get(key);
                const currentQty = Number(item?.actualQty ?? item?.QTY ?? item?.qty ?? 0);
                // Why no floor: a cashier can over-sell an item (scale weight
                // leftover, customer returns to add more, partial pack qty).
                // Clamping to 0 here silently discards the decrement — the
                // dirty row's `qty_delta` would still record the truth, but
                // the catalogue `QTY` would diverge from reality and the
                // next `applyLocalStockDeltas` call would compound the loss.
                // Letting qty go negative matches reality; a defensive guard
                // belongs at the till (warning at sale time), not silently
                // in the persistence layer.
                const nextQty = currentQty - decrement;
                const patched = {
                    ...item,
                    QTY: nextQty,
                    actualQty: nextQty,
                    updated_at: localUpdatedAt,
                };
                updated.push(patched);
                return patched;
            });

            await setLegacyStock(next);
            return updated;
        }

        await ensureMigratedFromLegacy(storage);

        const apply = async () => {
            const updated = [];
            for (const delta of deltas) {
                const row = await storage.get(
                    'SELECT qty, payload FROM stock_items WHERE lower(plu) = lower(?) LIMIT 1',
                    [delta.plu]
                );
                if (!row) continue;

                let payload;
                try {
                    payload = row.payload ? JSON.parse(row.payload) : {};
                } catch (_) {
                    payload = {};
                }

                const currentQty = Number(payload?.actualQty ?? payload?.QTY ?? row.qty ?? 0);
                // Same reasoning as the IndexedDB branch above: no floor on
                // the decrement. Over-sell is the cashier's reality, not a
                // corruption event. The dirty `qty_delta` is the source of
                // truth for reconciliation; this catalogue qty mirrors it.
                const nextQty = currentQty - delta.qtyDelta;

                const patched = {
                    ...payload,
                    PLU: payload?.PLU ?? delta.plu,
                    QTY: nextQty,
                    actualQty: nextQty,
                    updated_at: localUpdatedAt,
                };

                await storage.run(
                    `UPDATE stock_items
                     SET qty = ?, updated_at = ?, payload = ?
                     WHERE lower(plu) = lower(?)`,
                    [nextQty, localUpdatedAt, JSON.stringify(patched), delta.plu]
                );

                updated.push(patched);
            }
            return updated;
        };

        if (!useTransaction) {
            return apply();
        }
        return runTransaction(storage, apply);
    },

    async markDirtyPending(stockDeltas, saleId, options = {}) {
        const {
            localUpdatedAt = new Date().toISOString(),
            storage: providedStorage = null,
            useTransaction = true,
        } = options || {};
        const deltas = normalizePluDeltas(stockDeltas);
        if (deltas.length === 0) return 0;

        const storage = providedStorage || await getStorage();

        if (storage.platform === 'indexeddb') {
            const map = await getLegacyDirtyMap();
            for (const delta of deltas) {
                const key = toLower(delta.plu);
                const existing = map[key] || {};
                map[key] = {
                    plu: delta.plu,
                    local_updated_at: localUpdatedAt,
                    pending_sales: Math.max(0, Number(existing.pending_sales || 0)) + 1,
                    version: Math.max(0, Number(existing.version || 0)) + 1,
                    last_sale_id: saleId || existing.last_sale_id || null,
                    qty_delta: Number(existing.qty_delta || 0) + Number(delta.qtyDelta || 0),
                    updated_at: new Date().toISOString(),
                };
            }
            await setLegacyDirtyMap(map);
            return deltas.length;
        }

        await ensureSchema(storage);

        const apply = async () => {
            for (const delta of deltas) {
                await storage.run(
                    `INSERT INTO stock_dirty (plu, local_updated_at, pending_sales, version, last_sale_id, qty_delta, updated_at)
                     VALUES (lower(?), ?, 1, 1, ?, ?, CURRENT_TIMESTAMP)
                         ON CONFLICT(plu) DO UPDATE SET
                        local_updated_at = excluded.local_updated_at,
                                                 pending_sales = stock_dirty.pending_sales + 1,
                                                 version = stock_dirty.version + 1,
                                                 last_sale_id = excluded.last_sale_id,
                                                 qty_delta = COALESCE(stock_dirty.qty_delta, 0) + COALESCE(excluded.qty_delta, 0),
                                                 updated_at = CURRENT_TIMESTAMP`,
                    [delta.plu, localUpdatedAt, saleId || null, delta.qtyDelta]
                );
            }
            return deltas.length;
        };

        if (!useTransaction) {
            return apply();
        }
        return runTransaction(storage, apply);
    },

    async releaseDirtyPending(stockDeltas, saleId, options = {}) {
        const {
            storage: providedStorage = null,
            useTransaction = true,
        } = options || {};
        const deltas = normalizePluDeltas(stockDeltas);
        if (deltas.length === 0) return 0;

        const storage = providedStorage || await getStorage();

        if (storage.platform === 'indexeddb') {
            const map = await getLegacyDirtyMap();
            for (const delta of deltas) {
                const key = toLower(delta.plu);
                if (!map[key]) continue;
                map[key] = {
                    ...map[key],
                    pending_sales: Math.max(0, Number(map[key].pending_sales || 0) - 1),
                    last_sale_id: saleId || map[key].last_sale_id || null,
                    updated_at: new Date().toISOString(),
                };
                // Auto-clear when counter hits zero — closes the stale-dirty
                // trap where the row lingered after every sale upload.
                if (Number(map[key].pending_sales || 0) === 0) {
                    delete map[key];
                }
            }
            await setLegacyDirtyMap(map);
            return deltas.length;
        }

        await ensureSchema(storage);

        const apply = async () => {
            for (const delta of deltas) {
                await storage.run(
                    `UPDATE stock_dirty
                     SET pending_sales = CASE WHEN pending_sales > 0 THEN pending_sales - 1 ELSE 0 END,
                         last_sale_id = ?,
                         updated_at = CURRENT_TIMESTAMP
                     WHERE lower(plu) = lower(?)`,
                    [saleId || null, delta.plu]
                );
                // Auto-clear when counter hits zero — closes the stale-dirty
                // trap where the row lingered after every sale upload.
                await storage.run(
                    'DELETE FROM stock_dirty WHERE lower(plu) = lower(?) AND pending_sales = 0',
                    [delta.plu]
                );
            }
            return deltas.length;
        };

        if (!useTransaction) {
            return apply();
        }
        return runTransaction(storage, apply);
    },

    async getDirtyStockMap(pluList = []) {
        const storage = await getStorage();
        const normalizedPlu = normalizeUniquePluList(pluList);

        if (storage.platform === 'indexeddb') {
            const map = await getLegacyDirtyMap();
            if (normalizedPlu.length === 0) {
                return Object.entries(map).reduce((acc, [k, v]) => {
                    acc[toLower(k)] = v;
                    return acc;
                }, {});
            }
            return normalizedPlu.reduce((acc, plu) => {
                if (map[plu]) acc[plu] = map[plu];
                return acc;
            }, {});
        }

        await ensureSchema(storage);

        let rows = [];
        if (normalizedPlu.length > 0) {
            await runInClauseChunks(normalizedPlu, async (pluChunk) => {
                const inClause = buildInClause(pluChunk);
                const chunkRows = await storage.query(
                    `SELECT plu, local_updated_at, pending_sales, version, last_sale_id, qty_delta
                     FROM stock_dirty
                     WHERE lower(plu) IN ${inClause.clause}`,
                    inClause.params
                );
                rows = rows.concat(Array.isArray(chunkRows) ? chunkRows : []);
            });
        } else {
            rows = await storage.query(
                `SELECT plu, local_updated_at, pending_sales, version, last_sale_id, qty_delta
                 FROM stock_dirty`,
                []
            );
        }

        const dirtyMap = {};
        for (const row of rows || []) {
            if (!row?.plu) continue;
            dirtyMap[toLower(row.plu)] = row;
        }
        return dirtyMap;
    },

    async clearDirtyStock(pluList = []) {
        const storage = await getStorage();
        const normalizedPlu = normalizeUniquePluList(pluList);

        if (normalizedPlu.length === 0) return 0;

        if (storage.platform === 'indexeddb') {
            const map = await getLegacyDirtyMap();
            let removed = 0;
            for (const plu of normalizedPlu) {
                if (Object.prototype.hasOwnProperty.call(map, plu)) {
                    delete map[plu];
                    removed += 1;
                }
            }
            await setLegacyDirtyMap(map);
            return removed;
        }

        await ensureSchema(storage);
        let deleted = 0;
        await runInClauseChunks(normalizedPlu, async (pluChunk) => {
            const inClause = buildInClause(pluChunk);
            const result = await storage.run(
                `DELETE FROM stock_dirty WHERE lower(plu) IN ${inClause.clause}`,
                inClause.params
            );
            deleted += Number(result?.changes || 0);
        });
        return deleted;
    },

    /**
     * Delete every `stock_dirty` row whose `pending_sales = 0`.
     *
     * Defense-in-depth for the stale-dirty-row trap: when a sale uploads
     * and `releaseDirtyPending` decrements the counter to zero, the row
     * SHOULD be cleared by `clearDirtyStock`. If that hook is ever bypassed
     * (a missed call site, a crashed upload that left the row behind), the
     * next reconciliation will skip the PLU forever via Branch A. This
     * sweep is the safety net.
     *
     * IndexedDB legacy path deletes any row whose `pending_sales` is zero.
     * SQLite path deletes `WHERE pending_sales = 0` directly.
     *
     * @returns {Promise<number>} rows swept
     */
    async sweepStaleDirtyStock() {
        try {
            const storage = await getStorage();
            if (storage.platform === 'indexeddb') {
                const map = await getLegacyDirtyMap();
                let removed = 0;
                for (const key of Object.keys(map)) {
                    if (Number(map[key]?.pending_sales || 0) === 0) {
                        delete map[key];
                        removed += 1;
                    }
                }
                if (removed > 0) await setLegacyDirtyMap(map);
                return removed;
            }

            await ensureMigratedFromLegacy(storage);
            const result = await storage.run(
                'DELETE FROM stock_dirty WHERE pending_sales = 0',
                []
            );
            return Number(result?.changes || 0);
        } catch (err) {
            console.warn('[StockRepository] sweepStaleDirtyStock failed:', err?.message || err);
            return 0;
        }
    },

    /**
     * Delete every row in `stock_items` only.
     *
     * Used at the start of a successful paginated full sync (after page 0
     * is in hand) so local catalogue matches the server refill and
     * server-deleted PLUs cannot linger. Leaves `stock_dirty` intact so
     * offline pending-sale qty tracking survives the wipe.
     *
     * Callers must not use this for store switch / sign-out — use
     * {@link clearAllStock} there (clears dirty + catalogue).
     *
     * @returns {Promise<number>} always 0 (row count not required by callers)
     */
    async clearStockItemsOnly() {
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            // Legacy blob is the catalogue; dirty map is separate — keep it.
            await setLegacyStock([]);
            return 0;
        }

        await ensureSchema(storage);
        await runTransaction(storage, async () => {
            await storage.run('DELETE FROM stock_items', []);
        });
        return 0;
    },

    /**
     * Wipe the entire local catalogue and dirty map.
     * Used on sign-out and StoreDB change so the next login cannot
     * hydrate a previous store's stock (global stock_items is not
     * keyed by StoreDB; paginated sync only upserts).
     */
    async clearAllStock() {
        const storage = await getStorage();

        if (storage.platform === 'indexeddb') {
            await setLegacyStock([]);
            await setLegacyDirtyMap({});
            try {
                await setValueAdapter(LEGACY_ITEM_BARCODES_KEY, {});
            } catch (_) { /* ignore */ }
            return 0;
        }

        await ensureSchema(storage);
        await runTransaction(storage, async () => {
            await storage.run('DELETE FROM stock_items', []);
            await storage.run('DELETE FROM stock_dirty', []);
            try {
                await storage.run('DELETE FROM item_barcodes', []);
            } catch (_) { /* table may not exist yet */ }
        });
        return 0;
    },
};

export default StockRepository;
